import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CheckCircle, XCircle, Shield } from "lucide-react";

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="card-elev rounded-md p-6">
      <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-display text-3xl mt-3">{value}</div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [invs, setInvs] = useState([]);
  const [wds, setWds] = useState([]);

  const load = async () => {
    try {
      const [s, u, i, w] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/investments"),
        api.get("/admin/withdrawals"),
      ]);
      setStats(s.data); setUsers(u.data); setInvs(i.data); setWds(w.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const decideInv = async (id, approve) => {
    try {
      await api.post(`/admin/investments/${id}/decide`, { approve });
      toast.success(approve ? "Investment activated" : "Investment rejected");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const decideWd = async (id, approve) => {
    try {
      await api.post(`/admin/withdrawals/${id}/decide`, { approve });
      toast.success(approve ? "Withdrawal approved" : "Withdrawal rejected");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#07080a] text-slate-100 noise">
      <Navbar />
      <div className="pt-24 max-w-7xl mx-auto px-6 sm:px-10">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold mb-3">
          <Shield className="w-3.5 h-3.5" /> Admin console
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-10">
          Platform <span className="text-gold italic">controls.</span>
        </h1>

        <div className="grid md:grid-cols-5 gap-4 mb-10">
          <StatCard testid="ad-stat-users" label="Total users" value={stats?.total_users ?? 0} />
          <StatCard testid="ad-stat-pending-inv" label="Pending investments" value={stats?.pending_investments ?? 0} />
          <StatCard testid="ad-stat-active-inv" label="Active investments" value={stats?.active_investments ?? 0} />
          <StatCard testid="ad-stat-pending-wd" label="Pending withdrawals" value={stats?.pending_withdrawals ?? 0} />
          <StatCard testid="ad-stat-tvl" label="TVL" value={`$${(stats?.total_value_locked ?? 0).toLocaleString()}`} />
        </div>

        <Tabs defaultValue="investments">
          <TabsList data-testid="admin-tabs" className="bg-[#111317] border border-[#272a30]">
            <TabsTrigger data-testid="ad-tab-investments" value="investments">Investments</TabsTrigger>
            <TabsTrigger data-testid="ad-tab-withdrawals" value="withdrawals">Withdrawals</TabsTrigger>
            <TabsTrigger data-testid="ad-tab-users" value="users">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="investments">
            <div className="card-elev rounded-md p-6 mt-6 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#272a30]">
                    <TableHead className="text-slate-400">User</TableHead>
                    <TableHead className="text-slate-400">Package</TableHead>
                    <TableHead className="text-slate-400">Amount</TableHead>
                    <TableHead className="text-slate-400">Multiplier</TableHead>
                    <TableHead className="text-slate-400">Crypto / Tx</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invs.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10 font-mono-x text-sm">No investments yet.</TableCell></TableRow>
                  )}
                  {invs.map((i) => (
                    <TableRow key={i.id} data-testid={`ad-inv-${i.id}`} className="border-b border-[#272a30]">
                      <TableCell>
                        <div className="text-white">{i.user_name}</div>
                        <div className="text-xs text-slate-500 font-mono-x">{i.user_email}</div>
                      </TableCell>
                      <TableCell className="text-slate-300">{i.package_name}</TableCell>
                      <TableCell className="font-mono-x text-white">${i.amount.toLocaleString()}</TableCell>
                      <TableCell className="font-mono-x text-gold">{i.multiplier}×</TableCell>
                      <TableCell>
                        <div className="text-slate-300 text-sm">{i.crypto}</div>
                        <div className="text-xs text-slate-500 font-mono-x break-all max-w-[240px]">{i.tx_hash}</div>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded-full border uppercase tracking-widest ${
                          i.status === "active" ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/30" :
                          i.status === "pending" ? "bg-amber-500/10 text-amber-300 border-amber-400/30" :
                          i.status === "completed" ? "bg-cyan-500/10 text-cyan-300 border-cyan-400/30" :
                          "bg-red-500/10 text-red-300 border-red-400/30"
                        }`}>{i.status}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {i.status === "pending" ? (
                          <div className="flex gap-2 justify-end">
                            <Button data-testid={`ad-inv-approve-${i.id}`} size="sm" onClick={() => decideInv(i.id, true)} className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40">
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button data-testid={`ad-inv-reject-${i.id}`} size="sm" onClick={() => decideInv(i.id, false)} className="bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/40">
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-xs font-mono-x">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="withdrawals">
            <div className="card-elev rounded-md p-6 mt-6 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#272a30]">
                    <TableHead className="text-slate-400">User</TableHead>
                    <TableHead className="text-slate-400">Amount</TableHead>
                    <TableHead className="text-slate-400">Crypto</TableHead>
                    <TableHead className="text-slate-400">Destination</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wds.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10 font-mono-x text-sm">No withdrawal requests.</TableCell></TableRow>
                  )}
                  {wds.map((w) => (
                    <TableRow key={w.id} data-testid={`ad-wd-${w.id}`} className="border-b border-[#272a30]">
                      <TableCell>
                        <div className="text-white">{w.user_name}</div>
                        <div className="text-xs text-slate-500 font-mono-x">{w.user_email}</div>
                      </TableCell>
                      <TableCell className="font-mono-x text-white">${w.amount.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-300">{w.crypto}</TableCell>
                      <TableCell className="font-mono-x text-slate-400 text-xs break-all max-w-[220px]">{w.to_wallet}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded-full border uppercase tracking-widest ${
                          w.status === "approved" ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/30" :
                          w.status === "pending" ? "bg-amber-500/10 text-amber-300 border-amber-400/30" :
                          "bg-red-500/10 text-red-300 border-red-400/30"
                        }`}>{w.status}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {w.status === "pending" ? (
                          <div className="flex gap-2 justify-end">
                            <Button data-testid={`ad-wd-approve-${w.id}`} size="sm" onClick={() => decideWd(w.id, true)} className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40">
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button data-testid={`ad-wd-reject-${w.id}`} size="sm" onClick={() => decideWd(w.id, false)} className="bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/40">
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-xs font-mono-x">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="card-elev rounded-md p-6 mt-6 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#272a30]">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Role</TableHead>
                    <TableHead className="text-slate-400">Referral</TableHead>
                    <TableHead className="text-slate-400">Referred by</TableHead>
                    <TableHead className="text-slate-400">Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`ad-user-${u.id}`} className="border-b border-[#272a30]">
                      <TableCell className="text-white">{u.name}</TableCell>
                      <TableCell className="text-slate-300 font-mono-x text-sm">{u.email}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-gold/20 text-gold" : "bg-slate-500/10 text-slate-300"}`}>{u.role}</span></TableCell>
                      <TableCell className="font-mono-x text-gold">{u.referral_code}</TableCell>
                      <TableCell className="font-mono-x text-slate-400">{u.referred_by || "—"}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
